import snakeCase from 'lodash-es/snakeCase';
import * as moment from 'moment';
import { countryHasMasterAccount } from '~/pages/Onboarding/onboarding.constants';
import * as React from 'react';
import { useSelector } from 'react-redux';
import { getUserMetadataForSegment } from '~/selectors/user.selectors';
import User from '~/types/User';
import { identify } from '~/utils/segment/index';
import { Traits } from '~/utils/segment/schema';

/**
 * Formats an object to snake_case properties and date iso string
 * @param data
 */
const formatData = (data: Partial<User>): Traits => {
  if (!data) return data;

  /**
   * Moment object to ISO-8601
   * @param dateObject
   */
  const handleDate = (dateObject: moment.Moment | undefined): string | undefined => {
    return moment.isMoment(dateObject) ? (dateObject as moment.Moment).toISOString() : dateObject;
  };

  try {
    return Object.keys(data).reduce((traits, key) => {
      const snakeKey = snakeCase(key);
      switch (key) {
        case 'address':
          if (!data.address) return traits;
          return {
            ...traits,
            sender_address_company: data.address.company,
            sender_address_city: data.address.city,
            sender_address_state: data.address.state,
            sender_address_country_name: data.address.country?.name,
            sender_address_name: data.address.name,
            sender_address_phone: data.address.phone,
            sender_address_email: data.address.email,
          };
        case 'currentPromo':
          if (!data.currentPromo) return traits;
          return {
            ...traits,
            current_promo_code: data.currentPromo?.code,
            current_promo_start_date: handleDate(data.currentPromo?.startDate),
            current_promo_end_date: handleDate(data.currentPromo?.endDate),
          };
        case 'dateJoined':
          return {
            ...traits,
            [snakeKey]: handleDate(data[key]),
          };
        default:
          return {
            ...traits,
            [snakeKey]: data[key],
          };
      }
    }, {});
  } catch (error) {
    return {};
  }
};

/**
 * Monitors for user data changes and sets as Traits to Segment
 * @constructor
 */
const SegmentMonitor = () => {
  const [identified, setIdentified] = React.useState<boolean>(false);
  const user = useSelector(getUserMetadataForSegment);
  React.useEffect(() => {
    if (user && !identified) {
      const traits: Traits = {
        ...formatData(user),
        master_account_available: user.address?.country
          ? countryHasMasterAccount(user.address?.country)
          : undefined,
      };
      identify(user.id, traits as never);
      setIdentified(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);
  return null;
};

export default SegmentMonitor;
